import { useEffect, useState, useCallback } from "react";
import { Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { formatDateStringdot } from "../CommonJquery/CommonJquery";

export default function AdvanceOrder({ orderDatas, isLoading }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const handleViewOrder = useCallback((order) => {
    setSelectedOrder(order);
    setIsViewOpen(true);
  }, []);

  // Advance Order Details Modal
  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <Dialog open={isViewOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Advance Order Details - #{order.primary_id}</DialogTitle>
            <DialogDescription>
              Customer: {order.guest_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Order Info */}
            <div className="space-y-2">
              <h6 className="font-semibold text-foreground">Advance Order Information</h6>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Order No.</p>
                  <p className="font-semibold text-foreground">
                    {order.primary_id}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Order Type</p>
                  <p className="font-semibold text-foreground">
                    {{ 1: "Takeaway", 2: "Delivery", 3: "Reservation" }[
                      order.order_type
                    ] || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="font-semibold text-foreground">
                    {{
                      0: "Start",
                      1: "Prepare",
                      2: "Finish",
                      10: "Cancel",
                      3: "Hold",
                    }[order.current_status] || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Created Date</p>
                  <p className="font-semibold text-foreground">
                    {formatDateStringdot(order.entry_date)}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="space-y-2">
              <h6 className="font-semibold text-foreground">Customer Information</h6>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="font-semibold text-foreground">
                    {order.guest_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-semibold text-foreground">
                    {order.guest_mobile_no}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="font-semibold text-foreground">
                    {order.guest_address}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Locality</p>
                  <p className="font-semibold text-foreground">
                    {order.guest_locality}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">No. of Persons</p>
                  <p className="font-semibold text-foreground">
                    {order.no_of_guest}
                  </p>
                </div>
              </div>
            </div>

            {/* Amount Info */}
            <div className="space-y-2">
              <h6 className="font-semibold text-foreground">Amount Details</h6>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Item Amount</p>
                  <p className="font-semibold text-foreground">
                    ₹ {order.product_amount}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tax Amount</p>
                  <p className="font-semibold text-foreground">
                    ₹ {order.tax_amount}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Discount</p>
                  <p className="font-semibold text-foreground">
                    ₹ {order.discount_amount || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Delivery Charge</p>
                  <p className="font-semibold text-foreground">
                    ₹ {order.delivery_amount || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Packing Charge</p>
                  <p className="font-semibold text-foreground">
                    ₹ {order.packing_amount || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Final Amount</p>
                  <p className="font-semibold text-primary text-lg">
                    ₹ {order.final_amount}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="space-y-2">
              <h6 className="font-semibold text-foreground">Payment Information</h6>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Payment Status</p>
                  <p className="font-semibold text-foreground">
                    {order.paid_or_not == 1 ? "Paid" : "Not Paid"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Payment Mode</p>
                  <p className="font-semibold text-foreground">
                    {order.payment_mode || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Coupon Code</p>
                  <p className="font-semibold text-foreground">
                    {order.couponCode || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tip Amount</p>
                  <p className="font-semibold text-foreground">
                    ₹ {order.tip_amount || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Staff Info */}
            <div className="space-y-2">
              <h6 className="font-semibold text-foreground">Staff Information</h6>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Assigned To</p>
                  <p className="font-semibold text-foreground">
                    {order.assign_staff_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Items Count</p>
                  <p className="font-semibold text-foreground">
                    {order.item_data}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Items Table */}
            {order.details && order.details.length > 0 && (
              <div className="space-y-2">
                <h6 className="font-semibold text-foreground">Order Items</h6>
                <div className="rounded-lg border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground">
                          Item Name
                        </TableHead>
                        <TableHead className="text-muted-foreground">Qty</TableHead>
                        <TableHead className="text-muted-foreground">
                          Price
                        </TableHead>
                        <TableHead className="text-muted-foreground">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.details.map((item, index) => (
                        <TableRow key={index} className="border-border">
                          <TableCell className="font-semibold">
                            {item.item_name}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.item_qty}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            ₹ {item.item_amount}
                          </TableCell>
                          <TableCell className="font-semibold">
                            ₹ {item.total_amount}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="text-muted-foreground">Order No.</TableHead>
            <TableHead className="text-muted-foreground">Order Type</TableHead>
            <TableHead className="text-muted-foreground">Customer Name</TableHead>
            <TableHead className="text-muted-foreground">Phone</TableHead>
            <TableHead className="text-muted-foreground">Items</TableHead>
            <TableHead className="text-muted-foreground">Amount (₹)</TableHead>
            <TableHead className="text-muted-foreground">Tax (₹)</TableHead>
            <TableHead className="text-muted-foreground">Grand Total (₹)</TableHead>
            <TableHead className="text-muted-foreground">Status</TableHead>
            <TableHead className="text-muted-foreground">Created</TableHead>
            <TableHead className="text-muted-foreground text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!orderDatas || orderDatas.length === 0 ? (
            <TableRow>
              <TableCell colSpan="11" className="text-center text-muted-foreground py-4">
                No advance orders found
              </TableCell>
            </TableRow>
          ) : (
            orderDatas.map((order) => (
              <TableRow key={order.primary_id} className="border-border">
                <TableCell className="font-semibold text-foreground">
                  {order.primary_id}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {{ 1: "Takeaway", 2: "Delivery", 3: "Reservation" }[
                    order.order_type
                  ] || "N/A"}
                </TableCell>
                <TableCell className="font-semibold text-foreground">
                  {order.guest_name}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {order.guest_mobile_no}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {order.item_data}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {order.product_amount}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {order.tax_amount
                    ? Number(order.tax_amount).toFixed(2)
                    : "0.00"}
                </TableCell>
                <TableCell className="font-semibold text-foreground">
                  {order.final_amount}
                </TableCell>
                <TableCell>
                  <span className="inline-block px-2 py-1 text-xs rounded bg-primary/10 text-primary">
                    {{
                      0: "Start",
                      1: "Prepare",
                      2: "Finish",
                      10: "Cancel",
                      3: "Hold",
                    }[order.current_status] || "N/A"}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDateStringdot(order.entry_date)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewOrder(order)}
                      className="hover:bg-primary/10 hover:text-primary"
                      title="View Advance Order Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        onClose={() => setIsViewOpen(false)}
      />
    </div>
  );
}